import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertBanner,
  Button,
  EmptyState,
  Select,
  Skeleton,
  StatusBadge,
  TextInput,
  useLocale,
  useToast,
} from '@ministry/ui/internal';

import {
  getUser,
  updateUser,
  type AdminUserDetail,
  type AssignRoleResult,
  type RoleAssignment,
  type UpdateUserInput,
} from '../../lib/admin-users.js';
import { accountStatusEntry, assignmentLiveEntry, scopeLevelLabel } from '../../lib/admin-lifecycle.js';
import { ApiError, NetworkError } from '../../lib/api-client.js';
import { genericFieldErrorMessage } from '../../lib/field-errors.js';
import { formatInstantDisplay } from '../../lib/dates.js';
import { roleLabel } from '../../lib/roles.js';
import { useSession } from '../../lib/session.js';
import { useT } from '../../i18n/strings.js';
import { PageHeader } from '../../components/PageHeader.js';
import { ForbiddenState } from '../../components/ForbiddenState.js';
import { OrgUnitPicker } from './OrgUnitPicker.js';
import { GrantRoleModal } from './GrantRoleModal.js';
import { RevokeAssignmentModal } from './RevokeAssignmentModal.js';
import { DeactivateUserModal } from './DeactivateUserModal.js';
import { ADMIN_ROUTES } from './routes.js';

/**
 * SCREEN_SPECS.md I19 (detail half) — profile + "the user's current role assignments and
 * active sessions in context" (task brief item 2). `GET /users/{id}` already returns
 * both `roleAssignments` and `sessionCount` in one call, so this page reads them from
 * ONE query rather than three.
 */
export function UserDetailPage(): React.JSX.Element {
  const { userId } = useParams<{ userId: string }>();
  const t = useT();
  const { locale } = useLocale();
  const { push } = useToast();
  const queryClient = useQueryClient();
  const { data: session } = useSession();

  const [editing, setEditing] = React.useState(false);
  const [grantOpen, setGrantOpen] = React.useState(false);
  const [revokeTarget, setRevokeTarget] = React.useState<RoleAssignment | null>(null);
  const [deactivateOpen, setDeactivateOpen] = React.useState(false);
  const [enrolmentTicket, setEnrolmentTicket] = React.useState<{ token: string; expiresAt: string } | null>(null);

  const query = useQuery({
    queryKey: ['admin-user', userId],
    queryFn: ({ signal }) => getUser(userId as string, signal),
    enabled: Boolean(userId),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <span role="status" aria-live="polite" className="sr-only">
          {t('admin.userDetail.loading')}
        </span>
        <Skeleton shape="block" className="h-8 w-64" />
        <Skeleton shape="list-row" count={5} />
      </div>
    );
  }

  if (query.isError) {
    const error = query.error;
    if (error instanceof ApiError && (error.code === 'NOT_FOUND' || error.code.startsWith('FORBIDDEN'))) {
      return (
        <div className="flex flex-col gap-4">
          <PageHeader title={t('admin.userDetail.title')} breadcrumb={[{ label: t('admin.users.title'), href: ADMIN_ROUTES.users }]} />
          <ForbiddenState error={error} backTo={ADMIN_ROUTES.users} backLabel={t('admin.userDetail.backToDirectory')} />
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={t('admin.userDetail.title')} />
        <EmptyState
          title={t('admin.userDetail.error.title')}
          body={t('admin.userDetail.error.body')}
          action={
            <Button size="sm" variant="secondary" onClick={() => query.refetch()}>
              {t('admin.common.retry')}
            </Button>
          }
        />
      </div>
    );
  }

  const user = query.data as AdminUserDetail;
  const isSelf = session?.user.id === user.id;

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title={user.fullName}
        description={`@${user.username}`}
        breadcrumb={[
          { label: t('admin.users.title'), href: ADMIN_ROUTES.users },
          { label: user.fullName, href: ADMIN_ROUTES.userDetail(user.id) },
        ]}
        actions={<StatusBadge entry={accountStatusEntry(user.status)} />}
      />

      {enrolmentTicket ? (
        <AlertBanner
          variant="warning"
          title={t('admin.grant.mfaTicket.title')}
          onDismiss={() => setEnrolmentTicket(null)}
        >
          <p>{t('admin.grant.mfaTicket.body', { token: enrolmentTicket.token, expiresAt: formatInstantDisplay(enrolmentTicket.expiresAt, locale) })}</p>
        </AlertBanner>
      ) : null}

      <ProfileSection
        user={user}
        editing={editing}
        onEditingChange={setEditing}
        onSaved={() => {
          setEditing(false);
          invalidate();
          push({ variant: 'success', title: t('admin.userDetail.toast.saved') });
        }}
      />

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-heading">{t('admin.userDetail.status.title')}</h2>
          {user.status === 'active' && !isSelf ? (
            <Button variant="destructive" size="sm" onClick={() => setDeactivateOpen(true)}>
              {t('admin.userDetail.deactivate.action')}
            </Button>
          ) : null}
        </div>
        {isSelf ? <p className="text-sm text-text-muted">{t('admin.userDetail.deactivate.selfNote')}</p> : null}
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-heading">{t('admin.userDetail.roles.title')}</h2>
          <Button variant="primary" size="sm" onClick={() => setGrantOpen(true)} disabled={isSelf}>
            {t('admin.grant.action')}
          </Button>
        </div>
        {isSelf ? <p className="text-sm text-text-muted">{t('admin.grant.selfNote')}</p> : null}
        {user.roleAssignments.length === 0 ? (
          <EmptyState title={t('admin.userDetail.roles.empty.title')} body={t('admin.userDetail.roles.empty.body')} />
        ) : (
          <ul className="flex flex-col gap-2">
            {user.roleAssignments.map((assignment) => (
              <li
                key={assignment.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border-decorative p-3"
              >
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-text-primary">{roleLabel(assignment.roleCode, locale)}</span>
                    <StatusBadge entry={assignmentLiveEntry(assignment.live)} />
                  </div>
                  <span className="text-sm text-text-muted">
                    {scopeLevelLabel(assignment.scopeLevel, locale)}
                    {assignment.scopeOrgUnitCode ? ` · ${assignment.scopeOrgUnitCode}` : ''}
                    {' · '}
                    {t('admin.userDetail.roles.clearance', { rank: assignment.maxClearanceRank })}
                  </span>
                  <span className="text-xs text-text-muted">
                    {t('admin.userDetail.roles.validFrom', { date: formatInstantDisplay(assignment.validFrom, locale) })}
                    {assignment.validTo
                      ? ` · ${t('admin.userDetail.roles.validTo', { date: formatInstantDisplay(assignment.validTo, locale) })}`
                      : ''}
                  </span>
                </div>
                {assignment.live ? (
                  <Button variant="destructive" size="sm" onClick={() => setRevokeTarget(assignment)}>
                    {t('admin.revoke.action')}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-lg font-semibold text-text-heading">{t('admin.userDetail.sessions.title')}</h2>
        <p className="text-sm text-text-muted">{t('admin.userDetail.sessions.count', { count: user.sessionCount })}</p>
        <Link to={ADMIN_ROUTES.userSessions(user.id)} className="text-sm font-semibold text-text-link underline">
          {t('admin.userDetail.sessions.manage')}
        </Link>
      </section>

      <GrantRoleModal
        open={grantOpen}
        onOpenChange={setGrantOpen}
        userId={user.id}
        userName={user.fullName}
        onSuccess={(result: AssignRoleResult) => {
          invalidate();
          push({ variant: 'success', title: t('admin.grant.toast.success', { role: roleLabel(result.roleCode, locale) }) });
          if (result.mfaEnrolment) setEnrolmentTicket(result.mfaEnrolment);
        }}
      />
      <RevokeAssignmentModal
        open={revokeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRevokeTarget(null);
        }}
        userId={user.id}
        userName={user.fullName}
        assignment={revokeTarget}
        onSuccess={() => {
          invalidate();
          push({ variant: 'success', title: t('admin.revoke.toast.success') });
        }}
      />
      <DeactivateUserModal
        open={deactivateOpen}
        onOpenChange={setDeactivateOpen}
        userId={user.id}
        userName={user.fullName}
        onSuccess={() => {
          invalidate();
          push({ variant: 'success', title: t('admin.deactivate.toast.success', { name: user.fullName }) });
        }}
      />
    </div>
  );
}

function ProfileSection({
  user,
  editing,
  onEditingChange,
  onSaved,
}: {
  user: AdminUserDetail;
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
  onSaved: () => void;
}): React.JSX.Element {
  const t = useT();
  const { locale: displayLocale } = useLocale();
  const [fullName, setFullName] = React.useState(user.fullName);
  const [email, setEmail] = React.useState(user.email ?? '');
  const [locale, setLocaleValue] = React.useState<'ne' | 'en'>(user.locale);
  const [orgUnitId, setOrgUnitId] = React.useState<string | undefined>(user.primaryOrgUnitId ?? undefined);

  React.useEffect(() => {
    if (editing) {
      setFullName(user.fullName);
      setEmail(user.email ?? '');
      setLocaleValue(user.locale);
      setOrgUnitId(user.primaryOrgUnitId ?? undefined);
    }
  }, [editing, user]);

  const mutation = useMutation({
    mutationFn: (input: UpdateUserInput) => updateUser(user.id, input),
    onSuccess,
  });

  function onSuccess() {
    onSaved();
  }

  const isConflict = mutation.error instanceof ApiError && mutation.error.code === 'CONFLICT_STATE';

  const fieldErrorFor = (field: string): string | undefined => {
    if (!(mutation.error instanceof ApiError)) return undefined;
    const match = mutation.error.fieldErrors.find((fe) => fe.field === field);
    if (!match) return undefined;
    return genericFieldErrorMessage(t, match.code, match.params);
  };

  if (!editing) {
    return (
      <section className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-heading">{t('admin.userDetail.profile.title')}</h2>
          <Button variant="secondary" size="sm" onClick={() => onEditingChange(true)}>
            {t('admin.userDetail.profile.edit')}
          </Button>
        </div>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-text-muted">{t('admin.userDetail.profile.email')}</dt>
            <dd className="text-text-primary">{user.email ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-text-muted">{t('admin.userDetail.profile.locale')}</dt>
            <dd className="text-text-primary">{user.locale === 'ne' ? t('admin.userCreate.locale.ne') : t('admin.userCreate.locale.en')}</dd>
          </div>
          <div>
            <dt className="text-text-muted">{t('admin.userDetail.profile.mfa')}</dt>
            <dd className="text-text-primary">{user.mfaEnabled ? t('admin.userDetail.profile.mfaOn') : t('admin.userDetail.profile.mfaOff')}</dd>
          </div>
          <div>
            <dt className="text-text-muted">{t('admin.userDetail.profile.lastLogin')}</dt>
            <dd className="text-text-primary">
              {user.lastLoginAt ? formatInstantDisplay(user.lastLoginAt, displayLocale) : t('admin.userDetail.profile.never')}
            </dd>
          </div>
        </dl>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-3 rounded-md border border-border-decorative p-4">
      <h2 className="text-lg font-semibold text-text-heading">{t('admin.userDetail.profile.editTitle')}</h2>
      {isConflict ? (
        <AlertBanner variant="critical" title={t('admin.userDetail.profile.conflict')} />
      ) : mutation.isError && mutation.error instanceof NetworkError ? (
        <AlertBanner variant="critical" title={t('admin.error.network')} />
      ) : mutation.isError ? (
        <AlertBanner variant="critical" title={t('admin.error.checkFields')} />
      ) : null}
      <TextInput
        id="admin-user-edit-fullname"
        label={t('admin.userCreate.fullName.label')}
        required
        value={fullName}
        onChange={(event) => setFullName(event.target.value)}
        errorText={fieldErrorFor('fullName')}
      />
      <TextInput
        id="admin-user-edit-email"
        label={t('admin.userCreate.email.label')}
        type="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        errorText={fieldErrorFor('email')}
      />
      <Select
        id="admin-user-edit-locale"
        label={t('admin.userCreate.locale.label')}
        value={locale}
        onValueChange={(value) => setLocaleValue(value as 'ne' | 'en')}
        options={[
          { value: 'ne', label: t('admin.userCreate.locale.ne') },
          { value: 'en', label: t('admin.userCreate.locale.en') },
        ]}
      />
      <OrgUnitPicker
        id="admin-user-edit-orgunit"
        label={t('admin.userCreate.orgUnit.label')}
        value={orgUnitId}
        onValueChange={setOrgUnitId}
        errorText={fieldErrorFor('primaryOrgUnitId')}
      />
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="secondary" onClick={() => onEditingChange(false)} disabled={mutation.isPending}>
          {t('admin.common.cancel')}
        </Button>
        <Button
          variant="primary"
          loading={mutation.isPending}
          onClick={() =>
            mutation.mutate({
              expectedVersion: user.version,
              fullName: fullName.trim(),
              email: email.trim() === '' ? null : email.trim(),
              locale,
              primaryOrgUnitId: orgUnitId ?? null,
            })
          }
        >
          {mutation.isPending ? t('admin.common.saving') : t('admin.common.save')}
        </Button>
      </div>
    </section>
  );
}
